import { CreateTransient, Icon } from "@mindlercare/ui-web";
import { defaultTheme } from "@mindlercare/ui-web/src/shared/constants";
import { HTMLAttributes } from "react";
import { styled, css } from "styled-components";

export type RowPadding = "left" | "none";
export type RowStatus = "alert" | "info";

export const StyledTable = styled.table<{
  $noMargin?: boolean;
  $borderShadow?: boolean;
  $isDropdown?: boolean;
  $fixed?: boolean;
}>`
  display: table;
  position: ${(props) => (props.$isDropdown ? "absolute" : "relative")};
  z-index: ${(props) => (props.$isDropdown ? 1 : "auto")};
  border-collapse: collapse;
  width: 100%;
  table-layout: ${(props) => (props.$fixed ? "fixed" : "auto")};
  background-color: ${defaultTheme.COLORS.WHITE};
  box-shadow: ${(props) =>
    props.$borderShadow ? "0px 2px 8px rgba(29, 27, 27, 0.2)" : "none"};
  margin: ${(props) =>
    props.$noMargin ? "0" : `0 ${defaultTheme.PADDING.LG}`};
`;

export const StyledTd = styled.td<
  CreateTransient<{ width?: number; rowPadding?: RowPadding }>
>`
  display: table-cell;
  text-align: left;
  border-bottom: 1px solid ${defaultTheme.COLORS.GRAY_MEDIUM};
  padding: ${(props) => {
    switch (props.$rowPadding) {
      case "left":
        return `${defaultTheme.PADDING.LG} ${defaultTheme.PADDING.SM}`;
      case "none":
        return "0";
      default:
        return `${defaultTheme.PADDING.LG} 0`;
    }
  }};

  &:first-child {
    padding-left: 8px;
  }
`;

export const HoverVisibleCell = styled(StyledTd)<
  CreateTransient<{ rowPadding?: RowPadding }>
>`
  opacity: 0;
`;

export const StyledTh = styled.th<CreateTransient<{ rowPadding?: RowPadding }>>`
  text-align: left;
  height: 2.5rem;
  position: relative;
  padding: ${(props) => {
    switch (props.$rowPadding) {
      case "left":
        return `${defaultTheme.PADDING.LG} ${defaultTheme.PADDING.SM}`;
      case "none":
        return "0";
      default:
        return `${defaultTheme.PADDING.LG} 0`;
    }
  }};
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  cursor: pointer;
  min-height: 1rem;

  & > span {
    flex: 0 1 auto;
  }

  &::before {
    background-color: ${defaultTheme.COLORS.GRAY_DARK};
    border: 1px solid ${defaultTheme.COLORS.GRAY_DARK};
    border-radius: 4px;
    color: white;
    content: attr(data-title);
    display: none;
    font-size: 0.75rem;
    padding: 6px 8px;
    position: absolute;
    z-index: 1;
    top: calc(
      0.75rem + ${defaultTheme.PADDING.MD}
    ); /* Adjusted to fit the header */
    white-space: nowrap; /* Prevent line breaks */
    width: max-content; /* Fit text width */
  }

  &:hover::before {
    display: block;
  }
`;

export type StyledTrProps = HTMLAttributes<HTMLTableRowElement> &
  CreateTransient<{
    isClickable: boolean;
    rowStatus?: RowStatus;
  }> & {
    $rowId: string;
  };

export const StyledTr = styled.tr<StyledTrProps>`
  border-left: ${(props) => {
    switch (props.$rowStatus) {
      case "info":
        return `4px solid ${defaultTheme.COLORS.ACTION_BLUE}`;
      case "alert":
        return `4px solid ${defaultTheme.COLORS.ERROR}`;
      default:
        return "none";
    }
  }};

  &:hover {
    background-color: ${defaultTheme.COLORS.RIPPLE};
    cursor: ${(props) => (props.$isClickable ? "pointer" : "default")};

    & > td {
      text-decoration: "none";
    }

    ${HoverVisibleCell} {
      opacity: 1;
    }
  }
`;

const iconCss = css`
  color: ${defaultTheme.COLORS.GRAY_DARK};
  width: 0.75rem;
  height: 0.75rem;
`;

export const SortAscIcon = styled(Icon.SortAsc)`
  ${iconCss}
`;

export const SortDescIcon = styled(Icon.SortDesc)`
  ${iconCss}
`;

export const SortIconPlaceholder = styled.div`
  width: 0.75rem;
  height: 1.5rem;
`;
